// X-axis label collection + rotation/offset strategy helpers (pure functions)
// shared by InteractiveGraph renderers.
//
// MOVED here verbatim from InteractiveGraph.js (module-level pure functions;
// the only change is the `export ` keyword on each moved top-level declaration)
// as maintenance-only Slice 2 of the InteractiveGraph split — same pattern as
// Slice 1 (#42, utils/{chartLayout,legendMetrics,functionGraphLabels}.js).
// InteractiveGraph imports collectXAxisLabels / determineXAxisLabelStrategy
// back, so every call site is unchanged; estimateLabelLength is consumed only
// by determineXAxisLabelStrategy (and tests). Behavior must stay
// byte-identical: do NOT add component-/state-aware logic here.
//
// NOTE: applyAxisTextStyles intentionally stays in InteractiveGraph.js — it
// mutates a D3 selection (not a pure function) and is outside the Slice 2
// candidate set.

import { DEFAULT_LAYOUT, MIN_CHART_WIDTH } from './chartLayout';

// Full-width glyphs (kana, kanji, CJK punctuation, fullwidth forms) render at
// roughly twice the advance width of an ASCII glyph at the same font-size.
const FULL_WIDTH_RE = /[\u3000-\u30FF\u3400-\u4DBF\u4E00-\u9FFF\uF900-\uFAFF\uFF01-\uFF60\uFFE0-\uFFE6]/g;

/**
 * Width of a label in HALF-WIDTH UNITS (one unit ~= 0.5 * font-size px).
 *
 * Counting `String.length` treated 「名古屋」 as 3 while it draws as wide as six
 * ASCII characters, so Japanese labels systematically escaped rotation and
 * collided — one of the causes behind the 2026-08 student reports of
 * unreadable, overlapping axis labels.
 *
 * Measured per character, never per label-set: a label's width cannot depend on
 * what the OTHER labels contain. An earlier fix sampled `labels[0]` to decide
 * "is this Japanese", which mis-measured every mixed set (e.g. ['Jan','2月']).
 */
export const estimateLabelLength = (label) => {
  if (label === null || label === undefined || label === '') return 0;
  const str = label.toString();
  const fullWidth = (str.match(FULL_WIDTH_RE) || []).length;
  return str.length + fullWidth; // half-width chars 1, full-width 2
};

export const collectXAxisLabels = (graphType, actualGraphData) => {
  const elements = actualGraphData?.elements || [];
  if (!elements || elements.length === 0) return [];

  if (graphType === 'bar_chart') {
    return elements
      .filter((e) => e.type === 'bar')
      .map((bar) => {
          const dp0 = bar.data_points?.[0];
          return dp0?.x_label ?? dp0?.category ?? bar.label;
      })
      .filter(Boolean);
  }

  if (graphType === 'histogram') {
    return elements
      .filter((e) => e.type === 'histogram_bar')
      .map((bin) => bin.label || (bin.bin_range ? `${bin.bin_range[0]}-${bin.bin_range[1]}` : null))
      .filter(Boolean);
  }

  if (graphType === 'box_plot') {
    return elements
      .filter((e) => e.type === 'box')
      .map((box) => box.label)
      .filter(Boolean);
  }

  if (graphType === 'line_chart' || graphType === 'area_chart') {
    const line = elements.find((e) => e.type === 'line' && e.data_points?.length);
    return line ? line.data_points.map((p) => p.x_label || p.x).filter(Boolean) : [];
  }

  return [];
};

export const determineXAxisLabelStrategy = (labels = [], chartWidth = DEFAULT_LAYOUT.width, fontSizePx = 20) => {
  const baseStrategy = { rotation: 0, offsetX: 0, offsetY: 10, extraMargin: 0 };
  if (!labels.length) {
    return baseStrategy;
  }

  const longestLabel = Math.max(...labels.map((label) => estimateLabelLength(label)));
  const availableWidth = Math.max(chartWidth - 160, MIN_CHART_WIDTH);
  const averageSlotWidth = availableWidth / Math.max(labels.length, 1);

  // One half-width unit is about half the font-size, so this holds for Japanese
  // and Latin alike without branching on script. Font-size matters: the default
  // tick is 20px and students can enlarge it further, which is exactly when
  // labels collide — so the accessibility control must not make things worse.
  const estimatedLabelPixelWidth = longestLabel * fontSizePx * 0.5;

  // Rotation needed if:
  // (1) Label is inherently long (>= 8 chars), OR
  // (2) High label density (many labels in limited space), OR
  // (3) Estimated rendered width overflows the slot
  const densityScore = averageSlotWidth < 110 || labels.length > 8;
  const overflowsSlot = estimatedLabelPixelWidth > averageSlotWidth * 0.8;
  const requiresRotation = longestLabel >= 8 || densityScore || overflowsSlot;

  if (!requiresRotation) {
    return baseStrategy;
  }

  // Rotate harder when the label badly overflows its slot. Measured rather than
  // branched on script: a large font-size is only a problem in proportion to the
  // space available, and Japanese has no special claim on being wide.
  const severe = longestLabel >= 12
    || averageSlotWidth < 80
    || labels.length > 10
    || estimatedLabelPixelWidth > averageSlotWidth * 1.2;
  return {
    rotation: severe ? -55 : -35,
    offsetX: severe ? -12 : -8,
    offsetY: severe ? 32 : 24,
    extraMargin: severe ? 90 : 60
  };
};
